import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
import { ChevronLeft, Camera, Check, CircleAlert, Eye, EyeOff, ImagePlus, Info, Trash2, X } from "lucide-react";
import { ApiError, photoUrl } from "../lib/api";
import { isTouchDevice } from "../lib/device";
import { useI18n, type T } from "../lib/i18n";
import { compressImage, dataUrlToBlob, uploadPhoto } from "../lib/photo";
import { linkEarlyRead, startEarlyRead } from "../lib/ocr";
import { useBack, useRouter } from "../lib/router";
import { Mini, areaOf } from "./Tactile";

// ---------------------------------------------------------------------------------------------
// Errors

/** Human message for any thrown value. Feature code can pass extra code→key mappings. */
export function errorText(t: T, err: unknown, extra: Record<string, string> = {}) {
  if (err instanceof ApiError) {
    if (extra[err.code]) return t(extra[err.code], err.body);
    // The trial has its own, smaller daily photo allowance and says what Plus gives.
    const key = err.code === "ai_daily_limit" && err.body.trial ? "error.ai_daily_limit_trial" : `error.${err.code}`;
    const msg = t(key, err.body);
    if (msg !== key) return msg;
    if (err.code === "invalid_input" && err.body.message) return t("error.invalid_input_detail", { detail: err.body.message });
  }
  return t("error.generic");
}

// ---------------------------------------------------------------------------------------------
// Toasts

interface ToastItem {
  id: number;
  text: string;
  tone?: "bad" | "warn";
  action?: { label: string; run: () => void };
}
const ToastCtx = createContext<(t: Omit<ToastItem, "id">) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((t: Omit<ToastItem, "id">) => {
    const id = Date.now() + Math.random();
    setItems((xs) => [...xs.slice(-2), { ...t, id }]);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), t.action ? (t.tone === "warn" ? 9000 : 6000) : 3200);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {items.map((x) => (
          <div key={x.id} className={`toast ${x.tone ?? ""}`}>
            <span>{x.text}</span>
            {x.action && (
              <button
                onClick={() => {
                  x.action!.run();
                  setItems((xs) => xs.filter((y) => y.id !== x.id));
                }}
              >
                {x.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
export const useToast = () => useContext(ToastCtx);

// ---------------------------------------------------------------------------------------------
// Layout pieces

/**
 * The page's top bar: one compact row — a round back key, the part's own tile, the title, and any actions.
 * Every page except home has the back key (home and "Me" are always one tap away in the tab bar).
 * `free`: the page is one of the parts that are free forever, said with a small tag by the title.
 */
export function TopBar({ title, actions, fallback = "/", free }: { title?: ReactNode; back?: boolean; actions?: ReactNode; fallback?: string; free?: boolean }) {
  const goBack = useBack(fallback);
  const { t } = useI18n();
  const { route } = useRouter();
  const area = areaOf(route.path);
  return (
    <header className="topbar">
      <div className="topbar-inner">
        {route.path !== "/" && (
          <button className="round-btn" onClick={goBack} aria-label={t("common.back")}>
            <ChevronLeft size={24} strokeWidth={3} />
          </button>
        )}
        <Mini area={area} size="sm" />
        <div className="topbar-title">
          {free ? (
            <>
              {/* The name stays whole; the tag moves to a second line when the bar is narrow. */}
              <span className="topbar-name">{title}</span> <span className="free-badge topbar-free">{t("free.badge")}</span>
            </>
          ) : (
            title
          )}
        </div>
        {actions}
      </div>
    </header>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <span className="row muted" role="status">
      <span className="spinner" aria-hidden="true" />
      {label && <span className="small">{label}</span>}
    </span>
  );
}

export function Loading() {
  const { t } = useI18n();
  return (
    <div className="empty">
      <Spinner label={t("common.loading")} />
    </div>
  );
}

export function Empty({ icon, title, children, action }: { icon?: ReactNode; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      {icon}
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

export function Notice({ tone, children, icon }: { tone?: "warn" | "bad" | "good"; children: ReactNode; icon?: ReactNode }) {
  return (
    <div className={`notice ${tone ?? ""}`} role={tone === "bad" ? "alert" : undefined}>
      {icon ?? (tone === "good" ? <Check size={18} /> : tone ? <CircleAlert size={18} /> : <Info size={18} />)}
      <div>{children}</div>
    </div>
  );
}

export function LoadError({ error, retry }: { error: unknown; retry: () => void }) {
  const { t } = useI18n();
  return (
    <Notice tone="bad">
      <div className="stack" style={{ gap: 8 }}>
        <span>{errorText(t, error)}</span>
        <button className="btn small secondary" onClick={retry}>
          {t("common.retry")}
        </button>
      </div>
    </Notice>
  );
}

// ---------------------------------------------------------------------------------------------
// Sheet (bottom sheet on phones, centred dialog on larger screens)

export function Sheet({
  open,
  onClose,
  title,
  children,
  footer,
  dismissable = true,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** False while the sheet holds unsaved input: the close button and Escape then ask before discarding. */
  dismissable?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const { t } = useI18n();
  const titleId = useId();
  const [asking, setAsking] = useState(false);
  const openRef = useRef(open);
  openRef.current = open;
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    // iPhones before iOS 15.4 have no <dialog>: the sheet is then shown by hand (see .sheet-nodialog).
    if (typeof d.showModal !== "function") {
      d.classList.add("sheet-nodialog");
      d.toggleAttribute("open", open);
    } else {
      if (open && !d.open) d.showModal();
      if (!open && d.open) d.close();
    }
    if (!open) setAsking(false);
  }, [open]);
  const requestClose = () => (dismissable ? onClose() : setAsking(true));
  return (
    <>
      <dialog
        ref={ref}
        className="sheet"
        aria-labelledby={titleId}
        onCancel={(e) => {
          // A file picker inside the sheet (photo buttons) fires its own "cancel" when the person closes the
          // camera or photo library; on iPhone it bubbles up here. Only Escape on the sheet itself closes it.
          if (e.target !== e.currentTarget) return;
          e.preventDefault();
          requestClose();
        }}
        onClick={(e) => {
          if (dismissable && e.target === ref.current) onClose();
        }}
        onClose={() => {
          // The browser closed it by itself (e.g. Android's back button pressed repeatedly): keep the app in
          // step, or the sheet could never be opened again.
          if (openRef.current) onClose();
        }}
      >
        {open && (
          <>
            <div className="sheet-head">
              <h2 id={titleId}>{title}</h2>
              <button className="icon-btn" onClick={requestClose} aria-label={t("common.close")}>
                <X size={22} />
              </button>
            </div>
            <div className="sheet-body">{children}</div>
            {footer && <div className="sheet-foot">{footer}</div>}
          </>
        )}
      </dialog>
      {!dismissable && (
        <ConfirmSheet
          open={open && asking}
          title={t("common.discardTitle")}
          body={t("common.discardBody")}
          confirmLabel={t("common.discard")}
          cancelLabel={t("common.keepEditing")}
          danger
          onConfirm={onClose}
          onClose={() => setAsking(false)}
        />
      )}
    </>
  );
}

/** In-page confirmation (window.confirm is unreliable inside installed web apps). */
export function ConfirmSheet({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  danger,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  body?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  danger?: boolean;
  onConfirm: () => Promise<void> | void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <button className="btn secondary" onClick={onClose}>
            {cancelLabel ?? t("common.cancel")}
          </button>
          <button
            className={`btn ${danger ? "danger" : ""}`}
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onConfirm();
                onClose();
              } finally {
                setBusy(false);
              }
            }}
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      {body && <p>{body}</p>}
    </Sheet>
  );
}

// ---------------------------------------------------------------------------------------------
// Form controls

export function Field({ label, hint, error, children, htmlFor }: { label: ReactNode; hint?: ReactNode; error?: string | null; children: ReactNode; htmlFor?: string }) {
  return (
    <div className="field">
      <label htmlFor={htmlFor}>{label}</label>
      {children}
      {hint && !error && <span className="hint">{hint}</span>}
      {error && <span className="error">{error}</span>}
    </div>
  );
}

export function MoneyInput(props: InputHTMLAttributes<HTMLInputElement>) {
  const { lang } = useI18n();
  return (
    <div className="money-input">
      <input className="input num" inputMode="decimal" autoComplete="off" placeholder={lang === "nl" ? "0,00" : "0.00"} {...props} />
    </div>
  );
}

export function Segmented<V extends string>({ value, options, onChange, label }: { value: V; options: { value: V; label: ReactNode }[]; onChange: (v: V) => void; label?: string }) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Button({ busy, children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { busy?: boolean }) {
  return (
    <button {...rest} disabled={rest.disabled || busy} className={`btn ${rest.className ?? ""}`}>
      {busy && <span className="spinner" aria-hidden="true" />}
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------------------------
// Photo picker: camera or library → compressed → uploaded → photo id

export function PhotoPicker({
  photoId,
  onChange,
  label,
  compact,
  readsText,
}: {
  photoId: string | null;
  onChange: (id: string | null, sha256?: string) => void;
  label?: string;
  compact?: boolean;
  /** The photo's text will be read (receipts, labels): start reading on the phone while it uploads. */
  readsText?: boolean;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [preview, setPreview] = useState<string | null>(null);
  const camRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  /** Computers have no camera to open: they get a drop zone (drag a file in, or paste) instead. */
  const touch = useMemo(isTouchDevice, []);

  // On a computer, a pasted screenshot or copied image works too.
  useEffect(() => {
    if (touch) return;
    const onPaste = (e: ClipboardEvent) => {
      const file = [...(e.clipboardData?.files ?? [])].find((f) => f.type.startsWith("image/"));
      if (file) {
        e.preventDefault();
        pick(file);
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [touch]);

  async function pick(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    try {
      const dataUrl = await compressImage(file);
      // Show the photo at once, instead of an empty slot while it uploads.
      setPreview(dataUrl);
      const read = readsText ? Promise.resolve(dataUrl).then(dataUrlToBlob).then(startEarlyRead) : null;
      read?.catch(() => {});
      const r = await uploadPhoto(dataUrl);
      if (read) linkEarlyRead(photoUrl(r.id), read);
      onChange(r.id, r.sha256);
    } catch (e) {
      toast({ text: e instanceof ApiError ? errorText(t, e) : t("photo.unreadable"), tone: "bad" });
    } finally {
      setBusy(false);
      setPreview(null);
      if (camRef.current) camRef.current.value = "";
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  return (
    <div className="photo-slot">
      {label && <span className="label small" style={{ fontWeight: 650, color: "var(--ink-2)" }}>{label}</span>}
      {(photoId || preview) && <img src={preview ?? photoUrl(photoId!)} alt="" style={compact ? { maxHeight: 160 } : undefined} />}
      {busy && <Spinner label={t("photo.preparing")} />}
      {!touch && !photoId && (
        <button
          type="button"
          className={`photo-drop ${over ? "is-over" : ""}`}
          disabled={busy}
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            pick([...e.dataTransfer.files].find((f) => f.type.startsWith("image/")));
          }}
        >
          <ImagePlus size={28} aria-hidden="true" />
          <strong>{t("photo.drop")}</strong>
          <span className="small muted">{t("photo.dropSub")}</span>
        </button>
      )}
      <div className="btn-row">
        {touch && (
          <button type="button" className="btn small soft" disabled={busy} onClick={() => camRef.current?.click()}>
            <Camera size={18} /> {t("photo.camera")}
          </button>
        )}
        {(touch || photoId) && (
          <button type="button" className="btn small secondary" disabled={busy} onClick={() => fileRef.current?.click()}>
            <ImagePlus size={18} /> {touch ? t("photo.library") : t("photo.file")}
          </button>
        )}
        {photoId && (
          <button type="button" className="btn small ghost" onClick={() => onChange(null)} aria-label={t("photo.remove")}>
            <Trash2 size={18} />
          </button>
        )}
      </div>
      <input ref={camRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => pick(e.target.files?.[0])} />
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => pick(e.target.files?.[0])} />
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Data loading hook

export function useLoad<T>(fn: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const seq = useRef(0);
  const run = useCallback(async () => {
    const n = ++seq.current;
    setLoading(true);
    setError(null);
    try {
      const d = await fn();
      if (n === seq.current) setData(d);
    } catch (e) {
      if (n === seq.current) setError(e);
    } finally {
      if (n === seq.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => {
    run();
  }, [run]);
  return { data, setData, error, loading, reload: run };
}

/** Bar for a share of a total. `tone` colours over-limit bars. */
export function Bar({ label, value, share, tone }: { label: ReactNode; value: ReactNode; share: number; tone?: "warn" | "bad" }) {
  return (
    <div className="bar-row">
      <span className="bar-label">{label}</span>
      <span className="bar-value">{value}</span>
      <div className="bar-track" aria-hidden="true">
        <div className={`bar-fill ${tone ?? ""}`} style={{ width: `${Math.max(2, Math.min(100, share * 100))}%` }} />
      </div>
    </div>
  );
}

/** A password box with an eye button to show or hide what was typed. */
export function PasswordInput({ id, value, onChange, autoComplete, invalid }: { id: string; value: string; onChange: (v: string) => void; autoComplete: string; invalid?: boolean }) {
  const { t } = useI18n();
  const [shown, setShown] = useState(false);
  return (
    <div className="password-box">
      <input
        id={id}
        className="input"
        type={shown ? "text" : "password"}
        value={value}
        maxLength={128}
        autoComplete={autoComplete}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        aria-invalid={invalid || undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      <button type="button" className="password-eye" aria-label={t(shown ? "auth.hidePassword" : "auth.showPassword")} aria-pressed={shown} onClick={() => setShown((v) => !v)}>
        {shown ? <EyeOff size={20} /> : <Eye size={20} />}
      </button>
    </div>
  );
}
